import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import { works } from '@/mocks/works';
import { usePageSeo } from '@/hooks/usePageSeo';

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((w) => String(w.id) === id);

  usePageSeo({
    title: work ? `${work.title} — 小仓优子影视作品详解` : '作品未找到 — 小仓优子影视作品',
    description: work
      ? `《${work.title}》是小仓优子参演的${work.type}（${work.year}年），她在片中饰演${work.role}。本页提供作品资料、剧情简介与相关推荐。`
      : '未找到对应的影视作品信息。',
    keywords: work ? `${work.title},小仓优子,${work.type},${work.year}` : '小仓优子,影视作品',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!work) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-xl md:text-2xl text-foreground-950 mt-5">未找到该作品</h1>
          <p className="text-sm text-foreground-600 mt-3">该作品可能已被移除，或链接有误。</p>
          <Link
            to="/category/works"
            className="mt-6 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回影视作品列表
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = works
    .filter((w) => w.id !== work.id && (w.type === work.type || Math.abs(w.year - work.year) <= 2))
    .slice(0, 4);

  const infoRows = [
    { label: '类型', value: work.type },
    { label: '上映时间', value: `${work.year} 年` },
    { label: '导演', value: work.director },
    { label: '主要演员', value: work.cast.join('、') },
    { label: '小仓优子 饰', value: work.role },
    { label: '作品标签', value: work.tags.join(' · ') },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-gradient-to-br from-primary-50 via-background-50 to-secondary-50 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '影视作品', to: '/category/works' },
                { label: work.title },
              ]}
            />
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-1">
              <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img
                  src={work.cover}
                  alt={`${work.title} 封面`}
                  title={`${work.title} 小仓优子影视作品封面`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs">{work.type}</span>
                <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">{work.year} 年</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-4">{work.title}</h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">{work.intro}</p>

              <div className="mt-8 rounded-lg border border-background-200 overflow-hidden">
                <div className="px-5 py-3 bg-background-100 border-b border-background-200">
                  <h2 className="font-heading text-base text-foreground-950">作品资料</h2>
                </div>
                <dl className="divide-y divide-background-200">
                  {infoRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-3 gap-4 px-5 py-3.5">
                      <dt className="text-sm text-foreground-500">{row.label}</dt>
                      <dd className="col-span-2 text-sm text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-8">
                <Link
                  to="/category/works"
                  className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回作品分类
                </Link>
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看演员简介
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12 md:mt-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">作品简介</h2>
            <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.description}</p>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-12 md:mt-16">
              <div className="flex items-end justify-between mb-6">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">相关推荐</h2>
                <Link
                  to="/category/works"
                  className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  更多作品
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {related.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}